"use strict";
var __extends = (this && this.__extends) || (function () {
    var extendStatics = Object.setPrototypeOf ||
        ({ __proto__: [] } instanceof Array && function (d, b) { d.__proto__ = b; }) ||
        function (d, b) { for (var p in b) if (b.hasOwnProperty(p)) d[p] = b[p]; };
    return function (d, b) {
        extendStatics(d, b);
        function __() { this.constructor = d; }
        d.prototype = b === null ? Object.create(b) : (__.prototype = b.prototype, new __());
    };
})();
Object.defineProperty(exports, "__esModule", { value: true });
var React = require("react");
var PropTypes = require("prop-types");
var ReactDOM = require("react-dom");
var transitionInfo = require("../util/dom-helpers/transition");
var Events = require("../util/dom-helpers/events");
var classnames_1 = require("../util/classnames");
var _1 = require("../util/_");
var transitionEndEvent = transitionInfo.end;
exports.UNMOUNTED = 0;
exports.EXITED = 1;
exports.ENTERING = 2;
exports.ENTERED = 3;
exports.EXITING = 4;
/**
 * The Transition component lets you define and run css transitions with a simple declarative api.
 * It works similar to React's own [CSSTransitionGroup](http://facebook.github.io/react/docs/animation.html#high-level-api-reactcsstransitiongroup)
 * but is specifically optimized for transitioning a single child "in" or "out".
 *
 * You don't even need to use class based css transitions if you don't want to (but it is easiest).
 * The extensive set of lifecyle callbacks means you have control over
 * the transitioning now at each step of the way.
 */
var Transition = /** @class */ (function (_super) {
    __extends(Transition, _super);
    function Transition(props, context) {
        var _this = _super.call(this, props, context) || this;
        _this._needsUpdate = false;
        _this.performEnter = function (props) {
            _this.cancelNextCallback();
            var node = ReactDOM.findDOMNode(_this);
            // Not this.props, because we might be about to receive new props.
            if (props.onEnter) {
                props.onEnter(node);
            }
            _this.safeSetState({ status: exports.ENTERING }, function () {
                if (_this.props.onEntering) {
                    _this.props.onEntering(node);
                }
                _this.onTransitionEnd(node, function () {
                    _this.safeSetState({ status: exports.ENTERED }, function () {
                        if (_this.props.onEntered) {
                            _this.props.onEntered(node);
                        }
                    });
                });
            });
        };
        _this.performExit = function (props) {
            _this.cancelNextCallback();
            var node = ReactDOM.findDOMNode(_this);
            // Not this.props, because we might be about to receive new props.
            if (props.onExit) {
                props.onExit(node);
            }
            _this.safeSetState({ status: exports.EXITING }, function () {
                if (_this.props.onExiting) {
                    _this.props.onExiting(node);
                }
                _this.onTransitionEnd(node, function () {
                    _this.safeSetState({ status: exports.EXITED }, function () {
                        if (_this.props.onExited) {
                            _this.props.onExited(node);
                        }
                    });
                });
            });
        };
        _this.cancelNextCallback = function () {
            if (_this.nextCallback !== null) {
                _this.nextCallback.cancel();
                _this.nextCallback = null;
            }
        };
        _this.safeSetState = function (nextState, callback) {
            // This shouldn't be necessary, but there are weird race conditions with
            // setState callbacks and unmounting in testing, so always make sure that
            // we can cancel any pending setState callbacks after we unmount.
            _this.setState(nextState, _this.setNextCallback(callback));
        };
        _this.setNextCallback = function (callback) {
            var active = true;
            _this.nextCallback = function (event) {
                if (active) {
                    active = false;
                    _this.nextCallback = null;
                    callback(event);
                }
            };
            _this.nextCallback.cancel = function () {
                active = false;
            };
            return _this.nextCallback;
        };
        _this.onTransitionEnd = function (node, handler) {
            _this.setNextCallback(handler);
            if (node) {
                Events.on(node, transitionEndEvent, _this.nextCallback);
                setTimeout(_this.nextCallback, _this.props.timeout);
            }
            else {
                setTimeout(_this.nextCallback, 0);
            }
        };
        var initialStatus;
        if (props.in) {
            // Start enter transition in componentDidMount.
            initialStatus = props.transitionAppear ? exports.EXITED : exports.ENTERED;
        }
        else {
            initialStatus = props.unmountOnExit ? exports.UNMOUNTED : exports.EXITED;
        }
        _this.state = { status: initialStatus };
        _this.nextCallback = null;
        return _this;
    }
    Transition.prototype.componentDidMount = function () {
        if (this.props.transitionAppear && this.props.in) {
            this.performEnter(this.props);
        }
    };
    Transition.prototype.componentWillReceiveProps = function (nextProps) {
        if (nextProps.in && this.props.unmountOnExit) {
            if (this.state.status === exports.UNMOUNTED) {
                // Start enter transition in componentDidUpdate.
                this.setState({ status: exports.EXITED });
            }
        }
        else {
            this._needsUpdate = true;
        }
    };
    Transition.prototype.componentDidUpdate = function () {
        var status = this.state.status;
        if (this.props.unmountOnExit && status === exports.EXITED) {
            // EXITED is always a transitional state to either ENTERING or UNMOUNTED
            // when using unmountOnExit.
            if (this.props.in) {
                this.performEnter(this.props);
            }
            else {
                this.setState({ status: exports.UNMOUNTED });
            }
            return;
        }
        // guard ensures we are only responding to prop changes
        if (this._needsUpdate) {
            this._needsUpdate = false;
            if (this.props.in) {
                if (status === exports.EXITING) {
                    this.performEnter(this.props);
                }
                else if (status === exports.EXITED) {
                    this.performEnter(this.props);
                }
                // Otherwise we're already entering or entered.
            }
            else {
                if (status === exports.ENTERING || status === exports.ENTERED) {
                    this.performExit(this.props);
                }
                // Otherwise we're already exited or exiting.
            }
        }
    };
    Transition.prototype.componentWillUnmount = function () {
        this.cancelNextCallback();
    };
    Transition.prototype.render = function () {
        var status = this.state.status;
        if (status === exports.UNMOUNTED) {
            return null;
        }
        var _a = this.props, children = _a.children, className = _a.className;
        var childProps = _1.omit(this.props, Object.keys(Transition.propTypes));
        var transitionClassName;
        if (status === exports.EXITED) {
            transitionClassName = this.props.exitedClassName;
        }
        else if (status === exports.ENTERING) {
            transitionClassName = this.props.enteringClassName;
        }
        else if (status === exports.ENTERED) {
            transitionClassName = this.props.enteredClassName;
        }
        else if (status === exports.EXITING) {
            transitionClassName = this.props.exitingClassName;
        }
        var child = React.Children.only(children);
        return React.cloneElement(child, Object.assign({}, childProps, {
            className: classnames_1.classNames(child.props.className, className, transitionClassName)
        }));
    };
    Transition.propTypes = {
        in: PropTypes.bool,
        unmountOnExit: PropTypes.bool,
        transitionAppear: PropTypes.bool,
        timeout: PropTypes.number,
        exitedClassName: PropTypes.string,
        exitingClassName: PropTypes.string,
        enteredClassName: PropTypes.string,
        enteringClassName: PropTypes.string,
        onEnter: PropTypes.func,
        onEntering: PropTypes.func,
        onEntered: PropTypes.func,
        onExit: PropTypes.func,
        onExiting: PropTypes.func,
        onExited: PropTypes.func,
        className: PropTypes.string,
        children: PropTypes.node
    };
    Transition.defaultProps = {
        in: false,
        unmountOnExit: false,
        transitionAppear: false,
        timeout: 5000
    };
    return Transition;
}(React.Component));
exports.Transition = Transition;



//////////////////
// WEBPACK FOOTER
// ./node_modules/adp-react-components/lib/react-overlays/transition.js
// module id = 478
// module chunks = 0 1 2 3 4 5 6